import React, { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations, ContactShadows } from "@react-three/drei";

class GLBErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    return this.state.err ? this.props.fallback : this.props.children;
  }
}

function GLBModel({ url, scale, offsetY, still, idleClip, speakingBodyClip, speakingLipsClip }) {
  const group = useRef();
  const { scene, animations } = useGLTF(url);
  const { actions, names } = useAnimations(animations, group);

  // Molti .glb umanoidi includono una clip idle/breathing incorporata: se non la
  // suoniamo affatto, il rig resta nella bind pose (braccia larghe, il classico
  // "T-pose") — per questo "still" non ferma l'azione (stop()), la mette in pausa
  // (vedi sotto) finché l'ospite non interagisce (Inizia/microfono).
  // La prima clip in ordine di esportazione non è per forza quella di riposo — rig
  // esportati da Mixamo, ad es., spesso includono clip come "Landing" (atterraggio,
  // braccia aperte) prima di "Idle" nell'elenco. Tra le clip NON chiaramente di
  // un'azione (salto/atterraggio/intro/danza/...) si preferisce quella che sembra
  // un idle per nome, poi una qualunque di quelle non-azione, e solo se non ce n'è
  // nessuna si torna alla prima in assoluto. idleClip (dal pannello Avatar, vedi
  // models.py::ProjectAvatarConfig.idle_animation_clip): se configurata e presente nel
  // modello, scavalca del tutto questa euristica.
  //
  // Per QUESTO avatar (confermato dal cliente) la clip è unica e parte con le
  // braccia aperte per circa un secondo prima di assestarsi: saltiamo quel tratto
  // (INTRO_SKIP_SECONDS) sia da fermi sia riprendendo a parlare — MAI a tempo 0.
  //
  // La causa reale per cui le braccia aperte rispuntavano ad ogni battuta anche
  // dopo quel fix (saltare l'incipit non bastava): `action.fadeIn(duration)` di
  // three.js non sfuma dal PESO attuale dell'azione, riparte sempre da peso 0 e
  // risale a 1 nel tempo dato — con una sola azione attiva, peso 0 = nessun
  // contributo dell'animazione = si vede la bind pose del rig (braccia aperte),
  // per tutta la rampa, ad ogni "inizia a parlare". Qui non c'è mai una seconda
  // clip con cui sfumare, quindi niente fadeIn/fadeOut: il peso resta sempre a 1
  // (il default di un'azione nuova) e la sola cosa che cambia tra "fermo" e
  // "attivo" è se il tempo avanza (paused) — zero rampe di peso, zero bind pose.
  const INTRO_SKIP_SECONDS = 1.1;

  // Modelli con animazioni "parlato" dedicate, SEPARATE dall'idle (richiesto esplicitamente
  // dal cliente per un modello con clip multiple: una per il corpo, una per il labiale —
  // sincronizzate per durata, pensate per essere suonate INSIEME sullo stesso mixer, non in
  // alternanza). Servono ENTRAMBE le clip per attivare questa modalità: una sola configurata
  // (es. solo il corpo, labiale lasciato vuoto) non avrebbe senso da sola, si ricade sul
  // comportamento di sempre (unica clip idle, pausa/riprende con "still").
  const hasSpeakingClips = Boolean(speakingBodyClip && speakingLipsClip && actions[speakingBodyClip] && actions[speakingLipsClip]);

  useEffect(() => {
    if (!names.length) return;
    const isIdleLike = (n) => /idle|breath|rest|relax|stand|loop|default/i.test(n);
    const isActionLike = (n) => /landing|jump|fall|drop|intro|dance|wave|attack|punch|kick|cheer/i.test(n);
    const idleName =
      (idleClip && actions[idleClip] ? idleClip : null) ||
      names.find((n) => isIdleLike(n) && !isActionLike(n)) ||
      names.find((n) => !isActionLike(n)) ||
      names[0];
    const idleAction = actions[idleName];

    if (hasSpeakingClips) {
      // Due clip (corpo + labiale) suonate INSIEME sullo stesso AnimationMixer mentre si
      // parla — si tratta di track separate (ossa del corpo vs morph target della bocca, non
      // le stesse proprietà), quindi three.js le combina senza conflitti, a differenza di due
      // clip che muovessero le STESSE ossa (lì servirebbe un vero crossfade, non questo). Idle
      // interamente fermo (stop(), non solo in pausa) mentre si parla: muove le stesse ossa
      // del corpo della clip "parlato", altrimenti si sommerebbero/confliggerebbero.
      const bodyAction = actions[speakingBodyClip];
      const lipsAction = actions[speakingLipsClip];
      if (!still) {
        idleAction?.stop();
        [bodyAction, lipsAction].forEach((action) => {
          if (!action.isRunning()) action.reset();
          action.play();
          action.paused = false;
        });
      } else {
        bodyAction.stop();
        lipsAction.stop();
        // Qui l'idle è una clip dedicata, pensata per girare in loop (occhi/lingua/corpo a
        // riposo), non l'unica clip "di sempre" di un vecchio modello con un incipit da
        // saltare una tantum: a differenza del ramo di retrocompatibilità sotto, NON la
        // mettiamo in pausa su un fotogramma fisso (altrimenti l'avatar si "blocca totale"
        // invece di tornare a riposo in modo naturale) — la lasciamo semplicemente girare.
        if (idleAction) {
          if (!idleAction.isRunning()) idleAction.reset();
          idleAction.play();
          idleAction.paused = false;
        }
      }
      return;
    }

    // Comportamento di sempre (nessuna clip "parlato" dedicata configurata): un'unica clip,
    // sempre in play(), "still" ne mette in pausa/riprende solo l'avanzamento del tempo — vedi
    // il commento sopra sul perché mai fadeIn/fadeOut qui.
    if (!idleAction) return;
    const duration = idleAction.getClip()?.duration || 0;
    const pastIntro = duration > INTRO_SKIP_SECONDS ? INTRO_SKIP_SECONDS : duration;
    idleAction.play();
    idleAction.time = pastIntro;
    idleAction.paused = still;
  }, [names, actions, still, idleClip, hasSpeakingClips, speakingBodyClip, speakingLipsClip]);

  useFrame((state) => {
    if (group.current && !still) {
      group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.15;
    }
  });

  return <primitive ref={group} object={scene} scale={scale} position={[0, offsetY, 0]} />;
}

function ProceduralAvatar({ speaking, still }) {
  const group = useRef();
  const mouth = useRef();
  const lidL = useRef();
  const lidR = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (group.current && !still) {
      group.current.position.y = Math.sin(t * 1.2) * 0.05;
      group.current.rotation.y = Math.sin(t * 0.4) * 0.22;
    }
    if (mouth.current) {
      const open = speaking ? 0.12 + Math.abs(Math.sin(t * 12)) * 0.55 : 0.06;
      mouth.current.scale.y = open;
    }
    // blink
    const blink = Math.sin(t * 2.3) > 0.98 ? 0.05 : 1;
    if (lidL.current) lidL.current.scale.y = blink;
    if (lidR.current) lidR.current.scale.y = blink;
  });
  return (
    <group ref={group}>
      <mesh position={[0, 0.2, 0]}>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial color="#2f6bff" roughness={0.35} metalness={0.15} />
      </mesh>
      <mesh position={[0, 0.15, 0.72]}>
        <sphereGeometry args={[0.62, 48, 48]} />
        <meshStandardMaterial color="#eef3ff" roughness={0.5} />
      </mesh>
      <mesh ref={lidL} position={[-0.24, 0.3, 1.12]}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh ref={lidR} position={[0.24, 0.3, 1.12]}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh ref={mouth} position={[0, -0.08, 1.16]}>
        <capsuleGeometry args={[0.16, 0.18, 8, 16]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
    </group>
  );
}

export default function AvatarCanvas({
  modelUrl, provider, speaking, backgroundUrl, modelScale = 1.2, modelOffsetY = -1.4, className, still = false,
  idleClip, speakingBodyClip, speakingLipsClip,
}) {
  const useGlb = provider === "readyplayerme" && modelUrl;
  return (
    <div
      className={`bg-cover bg-center ${className || ""}`}
      style={backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
      data-testid="avatar-canvas-wrap"
    >
      <Canvas camera={{ position: [0, 0.3, 4], fov: 42 }} dpr={[1, 2]}>
        <ambientLight intensity={0.75} />
        <directionalLight position={[3, 5, 4]} intensity={1.15} />
        <directionalLight position={[-4, 2, -2]} intensity={0.5} color="#7aa2ff" />
        <Suspense fallback={null}>
          {useGlb ? (
            <GLBErrorBoundary fallback={<ProceduralAvatar speaking={speaking} still={still} />}>
              <GLBModel
                url={modelUrl} scale={modelScale} offsetY={modelOffsetY} still={still}
                idleClip={idleClip} speakingBodyClip={speakingBodyClip} speakingLipsClip={speakingLipsClip}
              />
            </GLBErrorBoundary>
          ) : (
            <ProceduralAvatar speaking={speaking} still={still} />
          )}
        </Suspense>
        <ContactShadows position={[0, -1.5, 0]} opacity={0.35} scale={9} blur={2.5} far={4} />
      </Canvas>
    </div>
  );
}
